import { useState } from 'react';
import { Copy, CheckCircle2, ExternalLink } from 'lucide-react';

const BASE_URL = 'https://api.app.nurev.io/v1';

const SNIPPETS = {
  curl: `curl -X POST ${BASE_URL}/embeddings \\
  -H "Authorization: Bearer nrev_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "nomic-embed-text",
    "input": "Hello, world."
  }'`,
  python: `from openai import OpenAI

client = OpenAI(
    api_key="nrev_YOUR_KEY",
    base_url="${BASE_URL}",
)

resp = client.embeddings.create(
    model="nomic-embed-text",
    input="Hello, world.",
)
print(resp.data[0].embedding[:8])`,
  node: `import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.NUREV_API_KEY,
  baseURL: "${BASE_URL}",
});

const resp = await client.embeddings.create({
  model: "nomic-embed-text",
  input: "Hello, world.",
});
console.log(resp.data[0].embedding.slice(0, 8));`
} as const;

type Lang = keyof typeof SNIPPETS;

export function QuickstartScreen() {
  const [lang, setLang] = useState<Lang>('curl');
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(SNIPPETS[lang]);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-nurev-text">Quickstart</h1>
        <p className="mt-1 text-sm text-nurev-muted">
          OpenAI-compatible API. Point any OpenAI SDK at our base URL and
          you're set.
        </p>
      </header>

      <section className="card">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-nurev-purple-light">
          1. Get a key
        </h2>
        <p className="text-sm text-nurev-text-soft">
          Head to{' '}
          <a
            href="/keys"
            className="text-nurev-purple-light hover:underline"
          >
            API Keys
          </a>{' '}
          and create one. Keys are prefixed with{' '}
          <code className="code-pill">nrev_</code> and shown once at creation
          time.
        </p>
      </section>

      <section className="card">
        <div className="mb-4 flex items-end justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nurev-purple-light">
            2. Make a request
          </h2>
          <div className="flex gap-1 rounded-lg border border-nurev-border bg-nurev-surface-lift p-1">
            {(['curl', 'python', 'node'] as Lang[]).map(l => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition ${
                  l === lang
                    ? 'bg-brand-gradient text-white'
                    : 'text-nurev-text-soft hover:text-nurev-text'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="relative">
          <pre className="overflow-x-auto rounded-lg border border-nurev-border bg-nurev-bg p-4 font-mono text-xs leading-relaxed text-nurev-text">
            <code>{SNIPPETS[lang]}</code>
          </pre>
          <button
            onClick={copy}
            className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-md border border-nurev-border bg-nurev-surface px-2.5 py-1 text-xs text-nurev-text-soft hover:text-nurev-text"
          >
            {copied ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-green-400" /> Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" /> Copy
              </>
            )}
          </button>
        </div>
      </section>

      <section className="card">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-nurev-purple-light">
          3. Available endpoints
        </h2>
        <ul className="space-y-3 text-sm">
          <Endpoint
            method="POST"
            path="/v1/embeddings"
            desc="Vector embeddings — nomic, mxbai, e5, minilm"
          />
          <Endpoint
            method="POST"
            path="/v1/chat/completions"
            desc="Chat completions — OpenAI-shaped"
          />
          <Endpoint
            method="GET"
            path="/v1/models"
            desc="List available models"
          />
          <Endpoint
            method="POST"
            path="/v1/batch/embeddings"
            desc="Batch embeddings (high-throughput; webhook on complete)"
          />
        </ul>
        <a
          href="https://api.app.nurev.io/openapi.json"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center gap-1.5 text-sm text-nurev-purple-light hover:underline"
        >
          Full OpenAPI 3.1 spec <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </section>
    </div>
  );
}

function Endpoint({
  method,
  path,
  desc
}: {
  method: string;
  path: string;
  desc: string;
}) {
  return (
    <li className="flex items-center gap-3">
      <span className="inline-flex w-14 justify-center rounded bg-nurev-purple/20 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-nurev-purple-light">
        {method}
      </span>
      <code className="font-mono text-nurev-text">{path}</code>
      <span className="text-xs text-nurev-muted">— {desc}</span>
    </li>
  );
}
